import axios, { type AxiosError, type InternalAxiosRequestConfig } from 'axios';
import { appConfig } from '@/config/app';
import type { ApiResponse } from '@/types/api';
import type { LoginResult } from '@/types/auth';
import { useAuthStore } from '@/stores/auth.store';
import { normalizeAuthUser, preserveLeadershipTier } from '@/utils/auth-user';

export class ApiError extends Error {
  constructor(
    message: string,
    public readonly code: number,
    public readonly status?: number,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

const http = axios.create({
  baseURL: appConfig.apiBaseUrl,
  timeout: 30_000,
  headers: {
    'Content-Type': 'application/json',
  },
});

let refreshPromise: Promise<string | null> | null = null;

function getAccessToken(): string | null {
  return useAuthStore.getState().accessToken;
}

function getRefreshToken(): string | null {
  return useAuthStore.getState().refreshToken;
}

async function refreshAccessToken(): Promise<string | null> {
  const refreshToken = getRefreshToken();
  if (!refreshToken) {
    return null;
  }

  const response = await axios.post<ApiResponse<LoginResult>>(
    `${appConfig.apiBaseUrl}/auth/refresh-token`,
    { refreshToken },
    { headers: { 'Content-Type': 'application/json' } },
  );

  const payload = response.data;
  if (payload.code !== 0 || !payload.data) {
    return null;
  }

  useAuthStore.getState().setSession({
    accessToken: payload.data.accessToken,
    refreshToken: payload.data.refreshToken,
    user: preserveLeadershipTier(
      normalizeAuthUser(payload.data.user as LoginResult['user'] & Record<string, unknown>),
      useAuthStore.getState().user,
    ),
  });

  return payload.data.accessToken;
}

http.interceptors.request.use((config: InternalAxiosRequestConfig) => {
  const token = getAccessToken();
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  if (config.data instanceof FormData) {
    delete config.headers['Content-Type'];
  }
  return config;
});

http.interceptors.response.use(
  (response) => {
    const payload = response.data as ApiResponse<unknown>;

    if (payload && typeof payload === 'object' && 'code' in payload) {
      if (payload.code !== 0) {
        return Promise.reject(new ApiError(payload.message || 'Request failed', payload.code));
      }
      return payload.data;
    }

    return response.data;
  },
  async (error: AxiosError<ApiResponse<unknown>>) => {
    const status = error.response?.status;
    const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean };
    const apiMessage = error.response?.data?.message;

    if (status === 401 && originalRequest && !originalRequest._retry) {
      const url = originalRequest.url ?? '';
      const isAuthEndpoint =
        url.includes('/auth/wechat-phone-login') || url.includes('/auth/refresh-token');

      if (!isAuthEndpoint) {
        originalRequest._retry = true;

        if (!refreshPromise) {
          refreshPromise = refreshAccessToken().finally(() => {
            refreshPromise = null;
          });
        }

        let newToken: string | null = null;
        try {
          newToken = await refreshPromise;
        } catch {
          newToken = null;
        }

        if (newToken) {
          originalRequest.headers.Authorization = `Bearer ${newToken}`;
          return http.request(originalRequest);
        }

        useAuthStore.getState().clearSession();
      }
    }

    const rawMessage = apiMessage || error.message || 'Network error';
    const message =
      status === 401 && (rawMessage === 'Session expired' || rawMessage === 'Unauthorized')
        ? '登录已过期，请重新登录'
        : rawMessage;
    return Promise.reject(new ApiError(message, error.response?.data?.code ?? -1, status));
  },
);

export { http };

export async function request<T>(config: Parameters<typeof http.request<T>>[0]): Promise<T> {
  return http.request<T>(config) as Promise<T>;
}
